
import { API_BASE_URL } from '../config';
import { showToast } from './toastManager';
import { loadingManager } from './loadingManager';

interface RequestOptions extends RequestInit {
  token?: string;
}

export class APIError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

export const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token');
};

export const getUserUid = (): string | null => {
  return localStorage.getItem('user_uid');
};

export const setAuthToken = (token: string): void => {
  localStorage.setItem('access_token', token);
  window.dispatchEvent(new Event('auth-token-set'));
};

export const removeAuthToken = (): void => {
  localStorage.removeItem('access_token');
};

const _migrateLegacyImpFromLocalStorage = (): void => {
  const legacyToken = localStorage.getItem('impersonation_token');
  const legacyExp = localStorage.getItem('impersonation_expires_at');
  if (legacyToken || legacyExp) {
    localStorage.removeItem('impersonation_token');
    localStorage.removeItem('impersonation_expires_at');
  }
};

export const getImpersonationToken = (): string | null => {
  _migrateLegacyImpFromLocalStorage();
  const expiresAt = getImpersonationExpiresAt();
  if (expiresAt && Date.now() / 1000 >= expiresAt) {
    removeImpersonationToken();
    return null;
  }
  return sessionStorage.getItem('impersonation_token');
};

export const setImpersonationToken = (token: string): void => {
  sessionStorage.setItem('impersonation_token', token);
};

export const setImpersonationExpiresAt = (expiresAt: number): void => {
  sessionStorage.setItem('impersonation_expires_at', String(expiresAt));
};

export const getImpersonationExpiresAt = (): number | null => {
  const val = sessionStorage.getItem('impersonation_expires_at');
  return val ? parseInt(val, 10) : null;
};

export const removeImpersonationToken = (): void => {
  sessionStorage.removeItem('impersonation_token');
  sessionStorage.removeItem('impersonation_expires_at');
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

export const getTokenInfo = (): { exists: boolean; preview?: string } => {
  const token = getAuthToken();
  if (!token) {
    return { exists: false };
  }
  return {
    exists: true,
    preview: `${token.substring(0, 20)}...${token.substring(token.length - 10)}`,
  };
};

export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> => {
  const { token, headers = {}, ...restOptions } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  

  const authToken = token || getImpersonationToken() || getAuthToken();

  const isFormData = restOptions.body instanceof FormData;
  const requestHeaders: Record<string, string> = isFormData ? {} : {
    'Content-Type': 'application/json',
  };

  if (headers) {
    Object.entries(headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        requestHeaders[key] = value;
      }
    });
  }

  if (authToken) {
    requestHeaders['Authorization'] = `Bearer ${authToken}`;
  }

  const requestId = loadingManager.startLoading();

  try {
    const response = await fetch(url, {
      ...restOptions,
      headers: requestHeaders,
    });

    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    if (!response.ok) {
      let errorData;
      let errorMessage = 'Request failed';
      
      try {
        errorData = isJson ? await response.json() : await response.text();
        errorMessage = errorData?.detail || errorData?.message || errorMessage;
      } catch (parseError) {
        errorMessage = response.statusText || errorMessage;
      }

      if (response.status === 401) {
        try {
          const { clearAllUserState } = await import('./session');
          clearAllUserState();
        } catch {
        }
        if (typeof window !== 'undefined' && window.location.pathname !== '/sign-in') {
          window.location.href = '/sign-in';
        }
      }

      if (response.status === 403) {
        if (errorMessage === 'account_banned') {
          if (typeof window !== 'undefined') {
            window.location.href = '/banned';
          }
        } else {
          showToast(errorMessage, 'error');
        }
      }

      if (response.status >= 500) {
        showToast('Server error. Please try again later.', 'error');
      }

      throw new APIError(
        errorMessage,
        response.status,
        errorData
      );
    }

    if (response.status === 204) {
      return {} as T;
    }

    const result = isJson ? await response.json() : (await response.text() as T);
    
    loadingManager.stopLoading(requestId);
    
    return result;
  } catch (error: any) {
    loadingManager.stopLoading(requestId);
    if (
      error?.name === 'AbortError' ||
      (error instanceof DOMException && error.name === 'AbortError')
    ) {
      throw error;
    }
    if (error instanceof APIError) {
      throw error;
    }

    const errorMessage = error instanceof Error ? error.message : 'Network error';

    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      showToast('Unable to connect to server. Please check your internet connection.', 'error');
    } else {
      showToast('Connection error. Please try again.', 'error');
    }

    throw new APIError(
      errorMessage,
      0,
      error
    );
  }
};

export const api = {
  get: <T = any>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    }),

  postFormData: <T = any>(endpoint: string, formData: FormData, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    }),
};

export default api;

